import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Users } from "lucide-react";
import { TABLE_STATUS } from "./theme";

const API = process.env.REACT_APP_BACKEND_URL;

export function TableGrid({ tables, showCentre, title = "Tables", token, onChanged, onOpen }) {
  const [sel, setSel] = useState(null);
  const act = async (status) => {
    const cleaningDone = status === "available" && sel.live_status === "cleaning";
    const r = await fetch(`${API}/api/booking-tokens/tables/${cleaningDone ? "mark-cleaning-complete" : "update-status"}`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, center: sel.center, table_number: sel.table_number, status, reason: `Booking Intelligence - marked ${status}` }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) return toast.error(typeof d.detail === "string" ? d.detail : "Not allowed");
    toast.success(`Table ${sel.table_number} -> ${status}`);
    setSel(null);
    onChanged?.();
  };
  return (
    <div className="bi-panel" data-testid="bi-table-grid">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="bi-panel-title !mb-0">{title}</h2>
        <div className="flex flex-wrap gap-2 text-sm">{["available", "reserved", "occupied", "cleaning"].map((k) => <span key={k} className={`rounded-full border px-3 py-1 ${TABLE_STATUS[k].cls}`}>{TABLE_STATUS[k].label}</span>)}</div>
      </div>
      {!tables?.length ? <p className="text-base text-muted-foreground">No tables are set up for this centre yet (Tables → Setup).</p> : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 xl:grid-cols-8 gap-3">
          {tables.map((t) => {
            const st = TABLE_STATUS[t.status] || TABLE_STATUS.unavailable;
            return (
              <button key={`${t.center}-${t.table_number}`} type="button" onClick={() => setSel(t)} className={`aspect-square rounded-2xl border-4 p-2 text-center shadow-sm transition-transform hover:-translate-y-0.5 ${st.cls}`} data-testid={`bi-table-${t.center}-${t.table_number}`}>
                <div className="text-2xl font-bold">{t.table_number}</div>
                <div className="flex items-center justify-center gap-1 text-sm"><Users className="h-4 w-4" />{t.capacity}</div>
                <div className="text-sm font-semibold">{st.label}</div>
                {t.next_booking && <div className="truncate text-xs opacity-90">Next {String(t.next_booking.time_slot || "").split("-")[0]}</div>}
                {showCentre && <div className="text-[11px] opacity-80">{t.center}</div>}
              </button>);
          })}
        </div>)}
      {sel && (
        <Dialog open onOpenChange={(o) => !o && setSel(null)}>
          <DialogContent className="max-w-lg" data-testid="bi-table-detail">
            <DialogHeader><DialogTitle>Table {sel.table_number} · {sel.capacity} seats {sel.zone ? `· ${sel.zone}` : ""}</DialogTitle></DialogHeader>
            <p className="text-base">Status now: <b>{(TABLE_STATUS[sel.live_status] || {}).label || sel.live_status}</b></p>
            <p className="font-semibold">Bookings on this date</p>
            {!sel.bookings.length ? <p className="text-muted-foreground">No bookings allotted to this table.</p> : sel.bookings.map((b) => (
              <div key={b.booking_id} className="rounded-xl bg-[#fff8e8] p-3 text-base"><b>{b.time_slot}</b> — {b.guest_name} · {b.num_guests} guests · {b.status}</div>))}
            {token && (
              <div className="flex flex-wrap gap-2 pt-2">
                {onOpen && <Button className="h-12 bg-[#7a0f1d] text-base" onClick={() => onOpen("live-queue")} data-testid="bi-table-seat">Assign / seat guest (Live Queue)</Button>}
                {sel.live_status !== "available" && <Button variant="outline" className="h-12 text-base" onClick={() => act("available")} data-testid="bi-table-mark-available">Mark available</Button>}
                {sel.live_status !== "cleaning" && <Button variant="outline" className="h-12 text-base" onClick={() => act("cleaning")} data-testid="bi-table-mark-cleaning">Mark cleaning</Button>}
              </div>)}
          </DialogContent>
        </Dialog>)}
    </div>
  );
}
